"use client"

import { useQuery } from "@tanstack/react-query"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import type { Role } from "@/lib/auth/permissions.types"

export interface User {
  id: number
  uuid: string
  email: string
  role: Role
  name: string
  phone?: string
  website?: string
  description?: string
  timezone: string
  uses_dst: boolean
  active: boolean
  additional_permissions?: string[]
  disabled_permissions?: string[]
  invite_token?: string | null
  invite_expires?: string | null
  created_at: string
  updated_at: string
}

export const usersQueryKey = (tenantId: string | null) => ["users", tenantId] as const

/** Fetches users for the current tenant. */
export function useUsersQuery() {
  const { fetchWithAuth, isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const { activeTenantId, can } = usePermissions()
  const canViewUsers = can(["users.view"])

  const query = useQuery({
    queryKey: usersQueryKey(activeTenantId),
    enabled: isAuthenticated && canViewUsers && Boolean(activeTenantId),
    queryFn: async (): Promise<User[]> => {
      const response = await fetchWithAuth("/api/users", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      })
      if (!response.ok) throw new Error(await extractErrorMessage(response, "Failed to load users"))
      const data = (await response.json()) as { users?: User[] }
      return data.users ?? []
    },
  })

  return {
    ...query,
    data: query.data ?? [],
    isLoading: isAuthLoading || query.isLoading,
  }
}

export async function extractUsersErrorMessage(response: Response, fallback: string): Promise<string> {
  return extractErrorMessage(response, fallback)
}

async function extractErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data: unknown = await response.json()
    if (data && typeof data === "object" && "error" in data && typeof data.error === "string") {
      return data.error
    }
  } catch {
    // Non-JSON API errors are still valid failures.
  }
  return response.statusText ? `${fallback} (${response.statusText})` : fallback
}
